import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { WorkflowEditor } from '@/components/application/workflow/workflow-editor';
import { workflowService } from '@/data/mock-workflows';
import type { Workflow, WorkflowNode, WorkflowEdge } from '@/types/workflow';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';

export const WorkflowEditPage = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:workflows-admin', 'common']);
    const [workflow, setWorkflow] = useState<Workflow | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isNew = workflowId === 'new';

    useEffect(() => {
        if (!isNew && workflowId) {
            loadWorkflow();
        } else {
            // New workflow - initialize with empty nodes/edges
            setWorkflow({
                workflow_id: 'new',
                name: '',
                description: '',
                is_default: false,
                status: 'draft',
                nodes: [],
                edges: [],
                created_at: new Date(),
                updated_at: new Date(),
            });
            setIsLoading(false);
        }
    }, [workflowId, isNew]);

    const loadWorkflow = () => {
        setIsLoading(true);
        const found = workflowService.getById(workflowId!);
        if (found && found.is_default) {
            // Cannot edit default workflow
            navigate(`/admin/workflows/${workflowId}`);
            return;
        }
        setWorkflow(found || null);
        setIsLoading(false);
    };

    const handleSave = (nodes: WorkflowNode[], edges: WorkflowEdge[], accountIds?: string[]) => {
        if (!workflow) return;

        if (isNew) {
            // Create new workflow
            workflowService.create({
                name: workflow.name || 'Nuovo Workflow',
                description: workflow.description,
                status: 'draft',
                nodes,
                edges,
                account_ids: accountIds,
            });
            // Navigation handled by modal in WorkflowEditor
        } else {
            // Update existing workflow
            workflowService.update(workflow.workflow_id, {
                nodes,
                edges,
                account_ids: accountIds,
            });
            // Navigation handled by modal in WorkflowEditor
        }
    };

    const handleCancel = () => {
        // Always navigate back to workflows list
        navigate('/admin/workflows');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('common:loading')}</p>
                </div>
            </div>
        );
    }

    if (!workflow) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-primary mb-2">{t('workflows.notFound.title')}</h3>
                <p className="text-tertiary mb-4">{t('workflows.notFound.message')}</p>
                <button
                    onClick={() => navigate('/admin/workflows')}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-secondary shadow-xs ring-1 ring-primary"
                >
                    {t('workflows.notFound.backToList')}
                </button>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-120px)]">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary">
                <div className="flex items-center gap-4">
                    <Link to="/admin/workflows">
                        <Button size="sm" color="secondary" iconLeading={ArrowLeft}>
                            {t('workflows.detail.back')}
                        </Button>
                    </Link>
                    <h2 className="text-xl font-semibold text-primary">{workflowId ? t('workflows.edit.title') : t('workflows.new.title')}</h2>
                </div>
            </div>
            <WorkflowEditor
                initialNodes={workflow.nodes}
                initialEdges={workflow.edges}
                initialAccountIds={workflow.account_ids}
                onSave={handleSave}
                onCancel={handleCancel}
            />
        </div>
    );
};
